import * as React from "react";

import { cn } from "../utils";

export interface ISegmentedToggleOption<T extends string> {
  value: T;
  label: React.ReactNode;
  // Names the option for assistive tech when `label` is terse (a symbol, an
  // acronym); falls back to `label` when it is a plain string.
  ariaLabel?: string;
  // Tints the pill and label while this option is selected: "success" (mint) for
  // options that mean "on/active", "danger" (red) for "off/stopped" ones.
  tone?: "success" | "danger";
}

const PILL_TONE = {
  success: "bg-mint-100 ring-mint-400",
  danger: "bg-red-100 ring-red",
} as const;

const LABEL_TONE = {
  success: "text-mint-500",
  danger: "text-red",
} as const;

interface ISegmentedToggleProps<T extends string> {
  options: ISegmentedToggleOption<T>[];
  value: T;
  onValueChange: (value: T) => void;
  disabled?: boolean;
  // true: clicking anywhere on the track advances to the next option.
  // false: only the clicked option is selected (tab-like).
  cycleOnClick?: boolean;
  "aria-label": string;
  className?: string;
}

interface IHighlighterStyle {
  height: number;
  width: number;
  transform: string;
}

/**
 * A radio group drawn as a segmented control: a gray-100 track with a raised
 * white pill that slides under the active option — the same treatment as
 * TabsList. By default clicking anywhere on the track cycles to the next
 * option; with `cycleOnClick={false}` it selects the clicked option instead.
 */
export const SegmentedToggle = <T extends string>({
  options,
  value,
  onValueChange,
  disabled,
  cycleOnClick = true,
  "aria-label": ariaLabel,
  className,
}: ISegmentedToggleProps<T>) => {
  const activeIndex = Math.max(
    0,
    options.findIndex((option) => option.value === value),
  );

  const containerRef = React.useRef<HTMLDivElement>(null);
  const optionRefs = React.useRef<(HTMLLabelElement | null)[]>([]);

  const [highlighterStyle, setHighlighterStyle] =
    React.useState<IHighlighterStyle>({
      height: 0,
      width: 0,
      transform: "translate(0, 0)",
    });

  const updateHighlighter = React.useCallback(() => {
    const selected = optionRefs.current[activeIndex];
    const container = containerRef.current;
    if (!selected || !container) return;

    const containerRect = container.getBoundingClientRect();
    const selectedRect = selected.getBoundingClientRect();
    const containerStyle = window.getComputedStyle(container);

    const paddingLeft = Number.parseFloat(containerStyle.paddingLeft);
    const paddingTop = Number.parseFloat(containerStyle.paddingTop);
    const borderLeft = Number.parseFloat(containerStyle.borderLeftWidth);
    const borderTop = Number.parseFloat(containerStyle.borderTopWidth);

    const translateX =
      selectedRect.left - containerRect.left - paddingLeft - borderLeft;
    const translateY =
      selectedRect.top - containerRect.top - paddingTop - borderTop;

    setHighlighterStyle({
      height: selectedRect.height,
      width: selectedRect.width,
      transform: `translate(${translateX}px, ${translateY}px)`,
    });
  }, [activeIndex]);

  React.useLayoutEffect(() => {
    updateHighlighter();
  }, [updateHighlighter]);

  React.useEffect(() => {
    const observer = new ResizeObserver(updateHighlighter);
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [updateHighlighter]);

  const selectAt = (index: number) => {
    const option = options[(index + options.length) % options.length];
    if (option) onValueChange(option.value);
  };

  const cycle = () => {
    if (disabled) return;
    selectAt(activeIndex + 1);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    // In cycle mode the radio's own change is ignored, so Space/Enter cycle here.
    if (cycleOnClick && (e.key === " " || e.key === "Enter")) {
      e.preventDefault();
      cycle();
    } else if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      e.preventDefault();
      selectAt(activeIndex + 1);
    } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
      e.preventDefault();
      selectAt(activeIndex - 1);
    }
  };

  const groupName = React.useId();
  const selectedOption = options[activeIndex];
  const optionName = (option: ISegmentedToggleOption<T>) =>
    option.ariaLabel ??
    (typeof option.label === "string" ? option.label : option.value);

  return (
    <div
      ref={containerRef}
      role="radiogroup"
      aria-label={ariaLabel}
      onKeyDown={handleKeyDown}
      onClick={cycleOnClick ? cycle : undefined}
      className={cn(
        "relative flex items-center rounded-lg bg-gray-100 p-1",
        !disabled && "cursor-pointer",
        disabled && "cursor-not-allowed opacity-50",
        className,
      )}
    >
      <div
        aria-hidden="true"
        // The ring is what makes the active option legible against the
        // track — TabsList carries the same one (KREA-4503 found a plain
        // white pill too subtle to read at a glance).
        className={cn(
          "absolute rounded-md shadow-md ring-1 transition-[transform,width,height,background-color] duration-200 ease-in-out",
          selectedOption?.tone
            ? PILL_TONE[selectedOption.tone]
            : "bg-white ring-primary-200",
        )}
        style={highlighterStyle}
      />
      {options.map((option, index) => {
        const isSelected = index === activeIndex;
        return (
          <label
            key={option.value}
            ref={(el) => {
              optionRefs.current[index] = el;
            }}
            // cursor-pointer belongs HERE, not only on the track: the labels
            // cover nearly all of it, and a <label> does not inherit the
            // track's cursor — measured `default` on hover, which is the whole
            // reason the control read as non-clickable.
            className={cn(
              "relative z-10 flex flex-1 select-none items-center justify-center whitespace-nowrap rounded-md px-3 py-1 font-semibold text-sm transition-colors duration-200",
              isSelected
                ? option.tone
                  ? LABEL_TONE[option.tone]
                  : "text-primary"
                : "text-gray-600",
              !disabled && "cursor-pointer",
              !disabled && !isSelected && "hover:text-primary",
              disabled && "cursor-not-allowed",
            )}
          >
            <input
              type="radio"
              name={groupName}
              value={option.value}
              checked={isSelected}
              disabled={disabled}
              tabIndex={isSelected && !disabled ? 0 : -1}
              aria-label={optionName(option)}
              onChange={() => {
                if (!cycleOnClick) onValueChange(option.value);
              }}
              onClick={(e) => e.stopPropagation()}
              className="sr-only"
            />
            {option.label}
          </label>
        );
      })}
      <div aria-live="polite" className="sr-only">
        {selectedOption && `${optionName(selectedOption)} selected`}
      </div>
    </div>
  );
};
